<template>
    <div v-if="loading">
        {{ loadingMessage }}
    </div>
    <div v-else class="content">
        <div class="container-fluid">
            <!-- Titre & breadcrumb -->
            <div class="page-heading row justify-content-between align-items-center py-3">
                <div class="col-lg-4">
                    <h4 class="page-title">{{ title }}</h4>
                </div>
                <div class="col-auto order-2 d-none d-sm-block">
                    <ol class="breadcrumb m-0 py-2">
                        <li class="breadcrumb-item">
                            <router-link to="/installation-list">{{ $t('common.installerSpace') }}</router-link>
                        </li>
                        <li class="breadcrumb-item">
                            <router-link to="/members">{{ $t('nav.myTeam') }}</router-link>
                        </li>
                        <li class="breadcrumb-item active">{{ title }}</li>
                    </ol>
                </div>
            </div>

            <form @submit.prevent="handleSubmit" class="pb-4">
                <BTabs pills style="font-weight: 600;">
                    <hr class="mt-0 mb-3">
                    <BTab>
                        <template #title>
                            <span>{{ $t('member.tab.info') }}</span>
                        </template>
                        <div class="row">
                            <div class="col-lg-8 col-xl-9 d-flex flex-column">
                                <!-- Informations d'identification -->
                                <div class="card flex-shrink-0">
                                    <div class="card-header pb-0">
                                        <h4 class="header-title">{{ $t('account.identInfo') }}</h4>
                                    </div>
                                    <div class="card-body">
                                        <div class="row">
                                            <div class="mb-2 col-md-6">
                                                <label for="email" class="form-label">{{ $t('common.email') }}</label>
                                                <input type="email" class="form-control" v-model="member.email" required :placeholder="$t('account.emailPlaceholder')" />
                                            </div>
                                            <div class="mb-2 col-md-6">
                                                <label for="mobile" class="form-label">{{ $t('common.mobilePhone') }}</label>
                                                <vue-tel-input class="form-control" v-model="mobile_phone_number" @validate="onValidPhoneNumber" :validCharactersOnly="true" :inputOptions="{required:true, placeholder:$t('account.phonePlaceholder'), id:'phone-input'}"></vue-tel-input>
                                            </div>
                                        </div>
                                        <div class="mb-2 col-md-6">
                                            <div class="form-check" style="margin-bottom: 0.5rem;">
                                                <input type="checkbox" class="form-check-input" id="send-password-link" v-model="member.send_change_password_link" :disabled="member.id === 0" />
                                                <label class="form-check-label" for="send-password-link">{{ $t('member.sendPasswordLink') }}</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <!-- Informations personnelles -->
                                <div class="card card-h-100">
                                    <div class="card-header pb-0">
                                        <h4 class="header-title">{{ $t('account.personalInfo') }}</h4>
                                    </div>
                                    <div class="card-body">
                                        <div class="row">
                                            <div class="mb-2 col-md-6">
                                                <label for="nom" class="form-label">{{ $t('common.lastName') }}</label>
                                                <input type="text" class="form-control" id="nom" v-model="member.name" required maxlength="32" :placeholder="$t('member.lastNamePlaceholder')" />
                                            </div>
                                            <div class="mb-2 col-md-6">
                                                <label for="prenom" class="form-label">{{ $t('common.firstName') }}</label>
                                                <input type="text" class="form-control" id="prenom" v-model="member.first_name" required maxlength="32" :placeholder="$t('member.firstNamePlaceholder')" />
                                            </div>
                                            <div class="mb-2 col-md-6">
                                                <label for="societe" class="form-label">{{ $t('common.company') }}</label>
                                                <input type="text" class="form-control" id="societe" v-model="member.company_name" required maxlength="32" :placeholder="$t('member.companyPlaceholder')" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="card flex-shrink-0">
                                    <div class="card-header">
                                        <div class="d-flex align-items-center justify-content-between">
                                            <h4 class="header-title mb-0">{{ $t('account.receiveAlerts') }}</h4>
                                            <div class="form-check mb-0">
                                                <input type="checkbox" id="alertes" v-model="member.alert" :data-switch="true" />
                                                <label for="alertes" :data-on-label="$t('common.yes')" :data-off-label="$t('common.no')"></label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="col-lg-4 col-xl-3 d-flex flex-column">
                                <!-- Photo ou logo -->
                                <div class="card flex-grow-1">
                                    <div class="card-header pb-0">
                                        <h4 class="header-title">{{ $t('common.photoOrLogo') }}</h4>
                                    </div>
                                    <div class="card-body">
                                        <div class="d-flex h-100 flex-wrap justify-content-center align-items-center" ref="imageContainer">
                                            <!-- Affichage photo -->
                                            <div v-if="member.picture" class="media-viewer border rounded">
                                                <img :src="member.picture" alt="image" class="media-viewer-img object-fit-cover" :style="{ maxHeight: imageMaxHeight }">
                                                <div class="media-viewer-tools">
                                                    <button type="button" class="btn btn-white text-danger" @click="removePicture">
                                                        <i class="mdi mdi-delete me-1"></i>{{ $t('common.delete') }}
                                                    </button>
                                                </div>
                                            </div>
                                            <!-- Ajouter photo -->
                                            <div v-else class="media-add-file single-file">
                                                <button type="button" class="btn btn-secondary" @click="handleFileUpload">{{ $t('common.addFile') }}</button>
                                                <input type="file" id="addFileInput" class="d-none" ref="fileInput" @change="handleFileChange" />
                                                <span class="help-block mt-1">
                                                    <small>{{ $t('common.imageRequirements') }}</small>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </BTab>
                    <BTab>
                        <template #title>
                            <span>{{ $t('member.tab.authorizedInstallations') }}</span>
                        </template>
                        <div class="card flex-shrink-0">
                            <div class="card-body">
                                <div class="d-flex align-items-center justify-content-between">
                                    <h4 class="header-title mb-0">{{ $t('member.grantAllInstallations') }}</h4>
                                    <div class="form-check mb-0">
                                        <input type="checkbox" id="filter" v-model="member.full_access" :data-switch="true" />
                                        <label for="filter" :data-on-label="$t('common.yes')" :data-off-label="$t('common.no')"></label>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-show="!member.full_access" class="card">
                            <div class="card-header">
                                <div class="row align-items-center">
                                    <div class="col-md-4">
                                        <h4 class="header-title">{{ $t('member.authorizedInstallationsList') }}</h4>
                                    </div>
                                    <div class="col-md-8 mt-2 mt-md-0 text-end">
                                        <div class="d-flex">
                                            <input type="text" id="searchTrigger" class="form-control form-control-sm search-trigger" :placeholder="$t('common.search')" aria-controls="users-table" maxlength="64">
                                            <button type="button" class="btn ms-2 btn-primary" @click="grantAllInstallations()">
                                                <i class="mdi mdi-plus"></i><span class="d-none d-xxl-inline-block">{{ $t('member.allInstallations') }}</span>
                                            </button>
                                            <button type="button" class="btn ms-2 btn-soft-primary" @click="denyAllInstallations()">
                                                <i class="mdi mdi-minus"></i><span class="d-none d-xxl-inline-block">{{ $t('member.noInstallations') }}</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="card-body border-top">
                                <CustomDataTable ref="installationsTable" tableId="granted-installations-table" :columns="columns" :data="installations" :emptyTableLabel="$t('member.emptyInstallations')" :rowClick="rowClick" />
                            </div>
                        </div>
                    </BTab>
                    <BTab>
                        <template #title>
                            <span>{{ $t('member.authorizations') }}</span>
                        </template>
                        <div class="row">
                            <div class="col-xl-8">
                                <div class="card flex-shrink-0">
                                    <div class="card-body">
                                        <div class="d-flex align-items-center justify-content-between gap-3">
                                            <div>
                                                <h4 class="header-title mb-1">{{ $t('member.groupAdminTitle') }}</h4>
                                                <p class="text-muted mb-0">{{ $t('member.groupAdminDesc') }}</p>
                                            </div>
                                            <div class="form-check mb-0 flex-shrink-0">
                                                <input type="checkbox" id="group-admin" v-model="member.group_admin" :data-switch="true" />
                                                <label for="group-admin" data-on-label="Oui" data-off-label="Non"></label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="card flex-shrink-0">
                                    <div class="card-body">
                                        <div class="d-flex align-items-center justify-content-between gap-3">
                                            <div>
                                                <h4 class="header-title mb-1">{{ $t('member.canCreateTitle') }}</h4>
                                                <p class="text-muted mb-0">{{ $t('member.canCreateDesc') }}</p>
                                            </div>
                                            <div class="form-check mb-0 flex-shrink-0">
                                                <input type="checkbox" id="can-create" v-model="member.can_create" :data-switch="true" />
                                                <label for="can-create" data-on-label="Oui" data-off-label="Non"></label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </BTab>
                </BTabs>
                <!-- Actions du formulaire -->
                <div class="form-actions-sticky d-flex justify-content-center align-items-center">
                    <router-link to="/members" class="btn btn-outline-secondary me-2">{{ $t('common.back') }}</router-link>
                    <button type="submit" class="btn btn-primary">{{ $t('common.save') }}</button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import { useUserStore } from '@/stores/userStore';
import { showToast } from '@/services/toast';
import { BTabs, BTab } from 'bootstrap-vue-next';
import { useRoute } from 'vue-router';
import { onMounted, ref, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import Member from '@/models/member';
import CustomDataTable from '@/components/customDatatable.vue';

export default {
    name: 'MemberPage',
    components: {
        BTabs,
        BTab,
        CustomDataTable,
    },
    setup() {
        const userStore = useUserStore();
        const route = useRoute();
        const { t } = useI18n();
        const member = ref(null);
        const imageContainer = ref(null);
        const imageMaxHeight = ref("100px");
        const mobile_phone_number = ref(null);
        const validPhoneNumber = ref(false);
        const loading = ref(true);
        const loadingMessage = ref(t('common.loading'));
        const title = ref(null);
        const installations = ref(userStore.facilities);
        const installationsTable = ref(null);

        const columns = [
            {
                id: 0,
                title: t('member.installation'),
                data: 'name',
                orderable: true,
                searchable: true,
                visible: true,
                className: 'cell-name',
            },
            {
                id: 1,
                data: null,
                title: t('member.authorized'),
                orderable: false,
                searchable: false,
                className: 'cell-status',
                render: function (data, type, row) {
                    const isChecked = member.value.filtered_facilities.includes(row.id) ? 'checked' : '';
                    return `
                        <span class="cell-legend d-md-none">${t('member.authorized')}&nbsp;:</span>
                        <div class="d-inline-block">
                            <input class="group-toggle row-checked" type="checkbox" id="grantAccess${row.id}" row-action="grant-access" row-id="${row.id}" data-switch="success" ${isChecked} />
                            <label for="grantAccess${row.id}" data-on-label="${t('common.yes')}" data-off-label="${t('common.no')}" class="mb-0 d-block"></label>
                        </div>`;
                }
            }
        ];

        function rowClick(action, id, checked) {
            switch(action) {
                case 'grant-access':
                    grantAccess(id, checked);
                    break;
            }
        }

        function grantAccess(id, checked) {
            const list = member.value.filtered_facilities;
            const facilityId = Number(id);
            const index = list.indexOf(facilityId);
            if (checked) {
                if (index !== -1) return;
                list.push(facilityId);
            } else {
                if (index === -1) return;
                list.splice(index, 1);
            }
            installationsTable.value.refresh(installations.value);
        }

        async function handleSubmit() {
            if(validate()) {
                await updateMember();
            }
        }

        function onValidPhoneNumber({ valid }) {
            validPhoneNumber.value = valid;
            if(valid) {
                const input = document.getElementById('phone-input');
                if(input) {
                    input.setCustomValidity('');
                }
            }
        }

        function validate() {
            if(!validPhoneNumber.value) {
                displayError('phone-input', t('common.phoneInvalid'));
                return false;
            }
            return true;
        }

        function displayError(id, error) {
            const input = document.getElementById(id);
            input.setCustomValidity(error);
            input.reportValidity();
        }

        async function updateMember() {
            try {
                member.value.mobile_phone_number = mobile_phone_number.value.replaceAll(' ', '');
                await userStore.updateMember(member.value);
                member.value.send_change_password_link = false;
                showToast('success', t('common.updateSuccess'));
            } catch (error) {
                showToast('error', error);
            }
        }

        function handleFileChange(event) {
            const file = event.target.files[0];
            if (file) {
                if (file.type.startsWith('image/')) {
                    const reader = new FileReader();
                    reader.onload = () => {
                        const base64Image = reader.result;
                        member.value.picture = base64Image;
                    };
                    reader.readAsDataURL(file);
                } else {
                    showToast('error', t('common.invalidImage'));
                }
            }
        }

        function handleFileUpload() {
            document.getElementById('addFileInput').click();
        }

        function removePicture() {
            member.value.picture = '';
        }

        function setImageMaxHeight() {
            if(imageContainer.value) {
                const rect = imageContainer.value.getBoundingClientRect();
                imageMaxHeight.value = `${rect.height * 0.9}px`;
            }
        }

        function grantAllInstallations() {
            member.value.filtered_facilities = installations.value.map(i => i.id);
            installationsTable.value.refresh(installations.value);
        }

        function denyAllInstallations() {
            member.value.filtered_facilities = [];
            installationsTable.value.refresh(installations.value);
        }

        onMounted(async () => {
            try {
                const memberId = Number(route.query.id);
                if(memberId === 0) {
                    member.value = new Member();
                    member.value.company_name = userStore.installer.company_name;
                    title.value = t('member.addTitle');
                } else {
                    member.value = await userStore.loadMember(memberId);
                    mobile_phone_number.value = member.value.mobile_phone_number;
                    title.value = t('member.editTitle');
                }
                loading.value = false;
                await nextTick();
                setImageMaxHeight();
                window.addEventListener("resize", setImageMaxHeight);
            } catch(error) {
                loadingMessage.value = error;
            }
        });

        return {
            member,
            handleSubmit,
            handleFileChange,
            handleFileUpload,
            removePicture,
            updateMember,
            imageContainer,
            imageMaxHeight,
            mobile_phone_number,
            onValidPhoneNumber,
            loading,
            loadingMessage,
            title,
            columns,
            installations,
            rowClick,
            installationsTable,
            grantAllInstallations,
            denyAllInstallations,
        };
    }
};
</script>

<style scoped>
:deep(.vue-tel-input.form-control) {
    display: flex;
}

:deep(.vue-tel-input.form-control .vti__input) {
    color: var(--ct-primary-color);
}

:deep(.vue-tel-input.form-control .vti__dropdown) {
    padding: 0;
}

:deep(.vue-tel-input.form-control .vti__input::placeholder) {
    color: var(--ct-secondary-color);
    opacity: 1;
}

:deep(.vue-tel-input.form-control:focus),
:deep(.vue-tel-input.form-control:focus-within),
:deep(.vue-tel-input.form-control .vti__input:focus),
:deep(.vue-tel-input.form-control .vti__input:focus-visible),
:deep(.vue-tel-input.form-control .vti__input:-webkit-autofill),
:deep(.vue-tel-input.form-control .vti__input::-webkit-focus-ring-color) {
  outline: none !important;
  box-shadow: none !important;
  border-color: transparent !important;
}

:deep(.vue-tel-input.form-control:focus-within) {
  border: 1px solid var(--ct-border-color) !important;
}
</style>
